import { ActivityIndicator, Pressable, View, TextInput as _TextInput, TextInputKeyPressEvent } from "react-native"
import { useState, useEffect, useRef } from "react"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import Icon from "@react-native-vector-icons/material-design-icons"
import { useRouter } from "expo-router"

import { TextInput, Text } from "./core"
import { trpc } from "@src/utils"
import { ar } from "@src/i18n/ar"
import { Header } from "./Header"
import { Props } from "./scanner"

export function Scanner({ onSubmit, onSubmitUpc, hide, skipFetching }: Props) {
  const router = useRouter()
  const insets = useSafeAreaInsets()
  const [barcode, setBarcode] = useState("")
  const [upc, setUpc] = useState<string | null>(null)
  const inputRef = useRef<_TextInput>(null)
  const validUpc = (upc?.length ?? 0) > 2
  const product = trpc.products.getByUpc.useQuery(
    { upc: upc as string }, //
    { enabled: validUpc && !skipFetching, retry: 0 },
  )
  const utils = trpc.useUtils()

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 100)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") hide()
    }

    if (typeof window !== "undefined") {
      window.addEventListener("keydown", handleKeyDown)
      return () => window.removeEventListener("keydown", handleKeyDown)
    }
  }, [hide])

  useEffect(() => {
    if (product.data?.id) {
      utils.products.get.setData({ id: product.data.id }, product.data as any)
      onSubmit(product.data.id)
      hide()
    }
  }, [product.data?.id])

  const handleSubmit = () => {
    const trimmedBarcode = barcode.trim()
    if (trimmedBarcode.length === 0) return

    if (skipFetching) {
      onSubmitUpc?.(trimmedBarcode)
      hide()
    } else {
      setUpc(trimmedBarcode)
    }
  }

  const handleKeyPress = (e: TextInputKeyPressEvent) => {
    if (e.nativeEvent.key === "Enter") handleSubmit()
  }

  const handleAddProduct = () => {
    router.push(`/dashboard/products/create?upc=${upc}`)
    hide()
  }

  const handleClear = () => {
    setUpc(null)
    setBarcode("")
    inputRef.current?.focus()
  }

  const header = (
    <Header
      title={ar.barcodeScanner}
      canGoBack={false}
      right={
        <Pressable hitSlop={5} className="active:opacity-70 px-4 h-full justify-center items-center" onPress={hide}>
          <Icon name="close" size={24} />
        </Pressable>
      }
    />
  )

  const footer = (
    <View className="bg-gray-50 border-t border-gray-200 py-3 px-6" style={{ paddingBottom: Math.max(insets.bottom, 12) }}>
      <Text className="text-xs text-gray-500 text-center">{ar.cameraNotAvailableOnWeb}</Text>
    </View>
  )

  // Show error state when product lookup fails
  if (validUpc && product.isError) {
    const isNotFound = product.error?.data?.code === "NOT_FOUND"
    return (
      <>
        {header}
        <View className="flex-1 items-center justify-center px-6">
          <View className="w-full max-w-md items-center">
            {isNotFound ? (
              <Pressable className="active:opacity-30 py-4" onPress={handleAddProduct}>
                <Text className="text-[13px] font-semibold text-center">
                  {ar.noProductFoundWithBarcode} {upc} {ar.addProduct}
                </Text>
              </Pressable>
            ) : (
              <Text className="text-center py-4">{ar.somethingWentWrong}</Text>
            )}
            <Pressable onPress={handleClear} className="py-2">
              <Text className="text-center text-blue-500">{ar.searchForAnotherBarcode}</Text>
            </Pressable>
          </View>
        </View>
        {footer}
      </>
    )
  }

  // Show loading state
  if (validUpc && product.isLoading) {
    return (
      <>
        {header}
        <View className="flex-1 items-center justify-center px-6">
          <ActivityIndicator size="large" color="#3B82F6" />
          <Text className="text-gray-600 mt-4">{ar.lookingUpProduct}</Text>
        </View>
        {footer}
      </>
    )
  }

  return (
    <>
      {header}

      <View className="flex-1 items-center justify-center px-6">
        <View className="w-full max-w-md">
          {/* Icon */}
          <View className="items-center mb-8">
            <View className="bg-blue-100 rounded-full p-6 mb-4">
              <Icon name="barcode" size={64} color="#3B82F6" />
            </View>
            <Text className="text-xl font-semibold text-gray-800 mb-2">{ar.enterBarcode}</Text>
            <Text className="text-sm text-gray-600 text-center">{ar.typeOrPasteBarcode}</Text>
          </View>

          {/* Input */}
          <TextInput
            ref={inputRef}
            className="w-full bg-white border-2 border-gray-300 rounded px-4 py-4 text-lg text-gray-800 text-center mb-4 focus:border-blue-500"
            placeholder={ar.barcode}
            value={barcode}
            onChangeText={setBarcode}
            onSubmitEditing={handleSubmit}
            onKeyPress={handleKeyPress}
            keyboardType="number-pad"
            returnKeyType="done"
            autoFocus
            selectTextOnFocus
          />

          {/* Submit Button */}
          <Pressable
            className={`w-full rounded py-4 items-center ${barcode.trim().length > 0 ? "bg-blue-500 active:bg-blue-600" : "bg-gray-300"}`}
            onPress={handleSubmit}
            disabled={barcode.trim().length === 0}
          >
            <Text className={`text-lg font-semibold ${barcode.trim().length > 0 ? "text-white" : "text-gray-500"}`}>{ar.submit}</Text>
          </Pressable>

          {/* Helper Text */}
          <View className="mt-6 bg-gray-50 rounded p-4">
            <Text className="text-xs text-gray-600 text-center mb-2">{ar.pasteWithKeyboardTip}</Text>
            <Text className="text-xs text-gray-600 text-center">{ar.pressEscToClose}</Text>
          </View>
        </View>
      </View>

      {footer}
    </>
  )
}
